import { Box, Typography, Stack, useMediaQuery, useTheme } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getTierStatus, TierStatusList } from '../api/getTierStatus'
import BgImg2 from '../assets/vip/bg-2.png'
import { subtitleShadow, tableTypoBase, tableTypoShawdowV2 } from '../utils/styles'

export const allTiers = [
  { key: 'cooper', icon: require('../assets/vip/cooper.png') },
  { key: 'bronze', icon: require('../assets/vip/bronze.png') },
  { key: 'silver', icon: require('../assets/vip/silver.png') },
  { key: 'gold', icon: require('../assets/vip/gold.png') },
  { key: 'crystal', icon: require('../assets/vip/crystal.png') },
  { key: 'platinum', icon: require('../assets/vip/platinum.png') },
  { key: 'diamond', icon: require('../assets/vip/diamond.png') },
  { key: 'elite', icon: require('../assets/vip/elite.png') },
  { key: 'master', icon: require('../assets/vip/master.png') },
]

// after tierMap is built

interface VipTableRow {
  label: string
  values: string[] | string[][]
  sub?: string | string[]
  isSection?: boolean
}

const VIPTable = () => {
  const { data: tierStatus = [] } = useQuery<TierStatusList[]>({
    queryKey: ['getTierStatus'],
    queryFn: getTierStatus,
    staleTime: Infinity,
  })

  const tierMap = tierStatus.reduce(
    (acc, t) => {
      acc[t.name.toLowerCase()] = t
      return acc
    },
    {} as Record<string, TierStatusList>,
  )

  const getVal = (tier: string, key: keyof TierStatusList): string =>
    tierMap[tier]?.[key] !== undefined ? tierMap[tier][key].toLocaleString() : '-'
  const tiers = allTiers.filter((t) => tierMap[t.key.toLowerCase()])
  const toYesDash = (v: boolean) => (v ? 'YES' : '-')

  const rows: VipTableRow[] = [
    {
      label: 'Service Supported',
      sub: 'Tier Upgrade Requirement',
      values: tiers.map((t) => (tierMap[t.key] ? tierMap[t.key].min_points.toLocaleString() : '-')),
    },
    {
      label: 'Monthly Bonus',
      values: tiers.map((t) => getVal(t.key.toLowerCase(), 'monthly_bonus')),
    },
    {
      label: 'Birthday Bonus',
      values: tiers.map((t) => getVal(t.key, 'birthday_bonus')),
    },
    // {
    //   label: 'Gift & Treats',
    //   values: tiers.map((t) => getVal(t.key, 'gift_treats')),
    // },
    {
      label: 'Membership Renewal',
      values: [
        [
          '0',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
          'LIFETIME',
        ],
      ],
    },
    {
      label: 'Gift & Treats',
      sub: ['Tier Upgrade Bonus', 'Special Birthday Gift'],
      values: [
        tiers.map((t) => getVal(t.key, 'gift_treats')),
        tiers.map((t) => toYesDash(tierMap[t.key]?.special_birthday_gift ?? false)),
      ],
    },
  ]
  const theme = useTheme()
  const mdUp = useMediaQuery(theme.breakpoints.up('sm'))

  return (
    <>
      <Typography
        variant="h4"
        textAlign="center"
        fontWeight={800}
        sx={{
          letterSpacing: 2,
          color: 'light.main',
          mb: 4,
          ...subtitleShadow,
          WebkitTextStroke: `1px ${theme.palette.secondary.dark}`,
          WebkitTextFillColor: '#fff',
          // fontSize: { xs: "1.05rem", md: "2.125rem" },
        }}
      >
        VIP MEMBERSHIP REWARDS
      </Typography>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            minWidth: 900,
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            borderRadius: '24px',
            overflow: 'hidden',
            background: 'transparents',
            backgroundImage: `url(${BgImg2})`,
            backgroundSize: { xs: 'cover', md: 'contained' },
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: '2px solid #FF0000',
            padding: 2,
          }}
        >
          <colgroup>
            <col
              style={{
                width: mdUp ? '180px' : '130px',
                minWidth: mdUp ? '180px' : '130px',
                maxWidth: '180px',
              }}
            />
            {tiers.map((_, idx) => (
              <col key={idx} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  minWidth: mdUp ? '180px' : '130px',
                  maxWidth: '180px',
                  fontSize: mdUp ? '1.05rem' : '0.75rem',
                  borderRight: `1px solid ${theme.palette.light.main}`,
                  borderBottom: `1px solid ${theme.palette.light.main}`,
                  ...tableTypoShawdowV2,
                  ...tableTypoBase,
                  paddingLeft: 0,
                }}
              >
                VIP PRIVILEGES
              </th>
              {tiers.map((t) => (
                <th
                  key={t.key}
                  style={{
                    fontSize: mdUp ? '1.05rem' : '0.75rem',
                    textAlign: 'center',
                    borderBottom: `1px solid ${theme.palette.light.main}`,
                    ...tableTypoShawdowV2,
                    ...tableTypoBase,
                    padding: '0 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t.key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) =>
              row.isSection ? (
                <tr key={index}>
                  <td
                    colSpan={tiers.length + 1}
                    style={{
                      textAlign: 'left',
                      borderTop: `1px solid ${theme.palette.light.main}`,
                      borderBottom: `1px solid ${theme.palette.light.main}`,
                      fontSize: mdUp ? '1rem' : '0.65rem',
                      paddingRight: 2,
                      ...tableTypoShawdowV2,
                      ...tableTypoBase,
                    }}
                  >
                    {row.label}
                  </td>
                </tr>
              ) : (
                <tr key={index}>
                  <td
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: row.sub ? 'flex-end' : 'center',
                      textAlign: 'left',
                      color: 'light.main',
                      fontSize: mdUp ? '1.05rem' : '0.75rem',
                      minWidth: mdUp ? '180px' : '130px',
                      maxWidth: '180px',
                      borderRight: `1px solid ${theme.palette.light.main}`,
                      borderBottom:
                        index === rows.length - 1 || row.isSection
                          ? 'none'
                          : `1px solid ${theme.palette.light.main}`,
                      ...tableTypoBase,
                      padding: '8px 0',
                    }}
                  >
                    <span style={{ ...tableTypoShawdowV2 }}>{row.label}</span>
                    {row.sub && (
                      <Box sx={{ mt: 0.5 }}>
                        {Array.isArray(row.sub) ? (
                          row.sub.map((subItem, subIndex) => (
                            <Typography
                              key={subIndex}
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: 700,
                                color: 'light.main',
                                fontSize: mdUp ? '0.75rem' : '0.55rem',
                                letterSpacing: 0.5,
                                textShadow: 'none',
                                paddingRight: 2,
                                mb: subIndex < (row.sub as string[]).length - 1 ? 0.5 : 0,
                              }}
                            >
                              {subItem}
                            </Typography>
                          ))
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              color: 'light.main',
                              fontSize: mdUp ? '0.75rem' : '0.55rem',
                              letterSpacing: 0.5,
                              textShadow: 'none',
                              paddingRight: 2,
                            }}
                          >
                            {row.sub}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </td>
                  {Array.isArray(row.values[0])
                    ? // Handle multiple value arrays (multiple subs) - stack values vertically in each tier cell
                      tiers.map((_, tierIndex) => (
                        <td
                          key={tierIndex}
                          style={{
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: mdUp ? '1rem' : '0.65rem',
                            padding: '8px',
                            borderBottom:
                              index === rows.length - 1 || row.isSection
                                ? 'none'
                                : `1px solid ${theme.palette.light.main}`,
                            color: 'light.main',
                            verticalAlign: row.sub ? 'bottom' : 'middle',
                          }}
                        >
                          {(row.values as string[][]).map((valueArray, valueIndex) => (
                            <Box
                              key={valueIndex}
                              sx={{
                                mb: valueIndex < (row.values as string[][]).length - 1 ? 1 : 0,
                              }}
                            >
                              {valueArray[tierIndex]}
                            </Box>
                          ))}
                        </td>
                      ))
                    : // Handle single value array (single sub or no sub)
                      (row.values as string[]).map((val, i) => (
                        <td
                          key={i}
                          style={{
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: mdUp ? '1rem' : '0.65rem',
                            padding: '8px',
                            borderBottom:
                              index === rows.length - 1 || row.isSection
                                ? 'none'
                                : `1px solid ${theme.palette.light.main}`,
                            color: 'light.main',
                            verticalAlign: row.sub ? 'bottom' : 'middle',
                          }}
                        >
                          {val}
                        </td>
                      ))}
                </tr>
              ),
            )}
          </tbody>
        </Box>
      </Box>

      <Box
        mt={3}
        px={2}
        py={3}
        sx={{
          fontFamily: 'inherit',
          textTransform: 'uppercase',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="900"
          textAlign="center"
          gutterBottom
          sx={{ color: 'secondary.main' }}
        >
          *Terms & Condition
        </Typography>
        <Stack spacing={1}>
          {[
            'Auto VIP upgrade upon reaching required accumulated deposit.',
            'Bonus subject to 3x turnover. Maximum withdrawal capped at 888.',
            'Do not mix deposit with other bonuses.',
            'Valid for all slot games.',
          ].map((item, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{ pl: 1.5, color: 'light.main', fontWeight: '700' }}
            >
              • {item}
            </Typography>
          ))}
        </Stack>
      </Box>
    </>
  )
}

export default VIPTable
